/**
 * CurrentLocationTool - Picks a point and updates the active centerline
 * position via CurrentLocationService.
 *
 * Consumes mouse_click events only (returns false for drags / other events),
 * enabling coexistence with camera tools bound to the same button.
 *
 * Three picking strategies depending on viewport type:
 *
 * SliceViewport2D:
 *   Uses canvasToWorld() to convert canvas coordinates to world space, then
 *   projects the result onto the slice plane so the depth component matches
 *   the currently displayed slice.
 *
 * CprViewport2D:
 *   Maps the canvas Y coordinate to a normalized position along the centerline
 *   (0 = top/start, 1 = bottom/end), then resolves the corresponding node index.
 *
 * Other viewports (3D):
 *   Uses PickExecutor with a CellPicker against centerline actors to obtain
 *   a world-space hit directly from the geometry.
 */

import vtkProp3D from '@kitware/vtk.js/Rendering/Core/Prop3D';

import { CprViewport2D, DisplayFeatureType, SliceViewport2D } from '../..';
import { projectPointOntoPlane } from '../../domain';
import type { Vec3 } from '../../domain';
import { PickExecutor } from '../../interaction/pick-executor';
import type { InteractionEvent } from '../../interaction/types';
import type { CurrentLocationService } from '../../services';
import type { IViewport } from '../../viewports/types';
import type { ITool } from '../types';

export class CurrentLocationTool implements ITool {
    static readonly toolName = 'CurrentLocation';

    readonly id = CurrentLocationTool.toolName;
    readonly name = 'Current Location';
    readonly cursor = 'crosshair';
    readonly completed = false;

    private viewport: IViewport | null = null;

    constructor(private readonly locationService: CurrentLocationService) {}

    activate(viewport: IViewport): void {
        this.viewport = viewport;
    }

    deactivate(): void {
        this.viewport = null;
    }

    cancel(): void {}

    handleEvent(event: InteractionEvent): boolean {
        if (event.type !== 'mouse_click') return false;
        if (!this.viewport) return false;

        // CPR: map canvas Y to centerline position
        if (this.viewport instanceof CprViewport2D) {
            return this.handleCprClick(event, this.viewport);
        }

        // Slice: project canvas click onto slice plane / 3D: cell-pick centerline actors
        const worldPosition =
            this.viewport instanceof SliceViewport2D
                ? this.computeSliceHitPoint(event, this.viewport)
                : this.computePickedHitPoint(event);

        if (!worldPosition) return false;

        this.locationService.setCurrentWorldPosition(worldPosition);
        return true;
    }

    /**
     * Map canvas Y position to a centerline node index on a CPR viewport.
     * The CPR image is fit to viewport height, so normalizedY = canvasY / canvasHeight
     * directly corresponds to distance / totalLength along the centerline.
     */
    private handleCprClick(event: InteractionEvent, viewport: CprViewport2D): boolean {
        const centerline = this.locationService.getActiveCenterline();
        if (!centerline || centerline.totalLength <= 0) return false;

        const [, canvasY] = event.currentPoints.canvas;
        const canvasHeight = viewport.getOverlayLayer().getCssHeight();
        if (canvasHeight <= 0) return false;

        const normalizedY = Math.max(0, Math.min(1, canvasY / canvasHeight));
        const distance = normalizedY * centerline.totalLength;
        const nodeIndex = centerline.getIndexAtDistance(distance);

        this.locationService.setCurrentCenterlinePosition(centerline.id, nodeIndex);
        return true;
    }

    /**
     * Convert canvas coordinates to world space via canvasToWorld(), then
     * project onto the slice plane to set the correct depth.
     */
    private computeSliceHitPoint(event: InteractionEvent, viewport: SliceViewport2D): Vec3 | null {
        const [canvasX, canvasY] = event.currentPoints.canvas;
        const worldPoint = viewport.canvasToWorld(canvasX, canvasY);
        return projectPointOntoPlane(
            worldPoint,
            viewport.slicePlaneOrigin as Vec3,
            viewport.slicePlaneNormal as Vec3
        );
    }

    /**
     * Use PickExecutor to cell-pick against centerline geometry actors.
     */
    private computePickedHitPoint(event: InteractionEvent): Vec3 | null {
        if (!this.viewport) return null;

        const actors = this.collectCenterlineActors();
        if (actors.length === 0) return null;

        const pickExecutor = new PickExecutor(this.viewport);
        pickExecutor.addProps(actors);

        const hit = pickExecutor.pick(event);
        pickExecutor.dispose();

        return hit ? hit.worldPosition : null;
    }

    /**
     * Gather all VTK actors from centerline display objects in the viewport.
     */
    private collectCenterlineActors(): vtkProp3D[] {
        if (!this.viewport) return [];

        const displayObjects = this.viewport.findDisplayObjectsByFeatureType(DisplayFeatureType.CENTERLINE);
        const actors: vtkProp3D[] = [];

        for (const object of displayObjects) {
            actors.push(...object.getActors());
        }

        return actors;
    }
}
